import React from 'react';
import { Property } from '../../services/api';
import {
  StickyHeader,
  DesktopNav,
  DesktopPhonePill,
  BrandLogo,
} from '../../design-system/components';

interface MapHeaderProps {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  setSearchFocused: (v: boolean) => void;
  searchSuggestions: Property[];
  showSearchDropdown: boolean;
  handleSuggestionSelect: (p: Property) => void;
  onLocateClick: () => void;
}

// StickyHeader — 로고 (전 viewport) + DesktopNav (md+) + 검색 (center) + 우측 액션
// 사용자 피드백 (2026-05-19): 모바일 "뒤로" 버튼 제거 (메인 = 지도이므로 뒤로갈 곳 없음).
// 로고로 교체.
export const MapHeader: React.FC<MapHeaderProps> = ({
  searchQuery,
  setSearchQuery,
  setSearchFocused,
  searchSuggestions,
  showSearchDropdown,
  handleSuggestionSelect,
  onLocateClick,
}) => {
  return (
        <div className="absolute inset-x-0 top-0 z-[15]">
          <StickyHeader
            left={
              <div className="flex items-center gap-6">
                <BrandLogo />
                <DesktopNav />
              </div>
            }
            center={
              // 사용자 피드백 (2026-05-18): "검색창을 헤더에 위치 + 자동완성 검색"
              // 입력 시 properties 매칭, suggestion 클릭 → 지도 panTo + zoom in.
              <div className="relative mx-auto w-full max-w-[480px]">
                <div className="flex items-center gap-2 rounded-ca-md border border-ca-line bg-ca-surface px-3 py-1.5 focus-within:border-ink focus-within:bg-frame">
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#78716C" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 180)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchSuggestions.length > 0) {
                        e.preventDefault();
                        handleSuggestionSelect(searchSuggestions[0]);
                      } else if (e.key === 'Escape') {
                        setSearchQuery('');
                        setSearchFocused(false);
                      }
                    }}
                    placeholder="단지명·동·지하철로 검색"
                    className="flex-1 bg-transparent text-[12.5px] font-semibold text-ink placeholder:text-muted outline-none"
                    aria-label="단지 검색"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSearchQuery('');
                      }}
                      className="text-mute2 hover:text-ink"
                      aria-label="검색어 지우기"
                    >
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {showSearchDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1 max-h-[60vh] overflow-y-auto rounded-ca-md border border-ca-line bg-frame shadow-ca-2 z-30">
                    {searchSuggestions.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionSelect(p);
                        }}
                        className="block w-full px-3 py-2 text-left border-b border-ca-line-soft last:border-0 hover:bg-ca-surface"
                      >
                        <div className="text-[13px] font-bold text-ink truncate">
                      {p.title}
                      {p.status === '분양완료' && <span className="ml-1.5 text-[11px] font-semibold text-muted">분양완료</span>}
                    </div>
                        <div className="mt-0.5 text-[11.5px] text-muted truncate">{p.address}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            }
            centerMode="always"
            forceCompact={false}
            right={
              <>
                <DesktopPhonePill className="mr-1" />
                <button
                type="button"
                onClick={onLocateClick}
                className="flex h-[38px] w-[38px] items-center justify-center rounded-ca-md text-muted hover:bg-ca-surface hover:text-ink"
                aria-label="현재 위치"
              >
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="2" />
                </svg>
              </button>
              </>
            }
          />
        </div>
  );
};
